/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {ColDef} from 'ag-grid-community';
import {AutocompleteFilterComponent} from '../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../core/models/column-header.model';
// Authority view levels that are used to determine if a user can view certain navigation items
import {SecurityLevelModel} from '../models/security-level.model';

export const VIEW_AUTHORITY = {
  inActiveUserAuthority: 'inactive-user-view',
  userAuthority: 'user-view',
  regionalAdminAuthority: 'regional-admin-view',
  centralOfficeAdminAuthority: 'central-office-admin-view',
  systemAdminAuthority: 'system-admin-view'
};

export const INVALID_CHARACTERS = /[!@#$%^&*_+=[\]{};':"\\|,.<>?]/;

export const DISMISS_MSG = 'Dismiss';

export const VFUEL_RECORDS_EXCLUDED_HEADER = 'X-Vfuel-Records-Excluded';
export const VFUEL_RECORDS_EXCLUDED_MSG = 'Some VFuel values were unable to be mapped, so those records were excluded.';

export const VEHICLE_TYPES = ['CAR', 'TRUCK'];

export const DEFAULT_START_YEAR = 1900;
export const DEFAULT_END_YEAR = 2050;

export const FORBIDDEN_ERR_MSG = 'Action not allowed';

export const ALL = 'All';

// Portfolio type constants
export const BUSINESS_PLAN = 'Business Plan';
export const TVP = 'TVP';
export const GMTVP = 'GMTVP';

export const MANAGE_GFS_VEHICLES = 'manage-gfs-vehicles';

export const SEC_LEVEL_LIST: SecurityLevelModel[] = [
  {value: '2', viewValue: 'Central Office Admin'},
  {value: '3', viewValue: 'Regional Admin'},
  {value: '1', viewValue: 'System Admin'},
  {value: '4', viewValue: 'User'}
];

export const PROPULSION_COLUMN_HEADERS: ColumnHeader[] = [
  {name: 'propPortfolioId', label: 'PROP_PORTFOLIO_ID'},
  {name: 'iteration', label: 'ITERATION'},
  {name: 'portType', label: 'PORT_TYPE'},
  {name: 'recId', label: 'RECID'},
  {name: 'recIdProp', label: 'RECIDPROP'},
  {name: 'fcOrg', label: 'FCORG'},
  {name: 'propPortstatSub', label: 'PROP_PORTSTAT_SUB'},
  {name: 'propPortstatPm', label: 'PROP_PORTSTAT_PM'},
  {name: 'mktGrp', label: 'MKTGRP'},
  {name: 'region', label: 'REGION'},
  {name: 'subRegion', label: 'SUBREGION'},
  {name: 'sevenKeyFields.plngMkt', label: 'PLNGMKT'},
  {name: 'arch', label: 'ARCH'},
  {name: 'sevenKeyFields.prgmCode', label: 'PRGMCODE'},
  {name: 'sevenKeyFields.styleCode', label: 'STYLECODE'},
  {name: 'sevenKeyFields.bodyStyle', label: 'BODYSTYLE'},
  {name: 'vehType', label: 'VEHTYPE'},
  {name: 'sevenKeyFields.mktDiv', label: 'MKTDIV'},
  {name: 'sevenKeyFields.brand', label: 'BRAND'},
  {name: 'sevenKeyFields.lbrand', label: 'LBRAND'},
  {name: 'brandFam', label: 'BRANDFAM'},
  {name: 'drvPos', label: 'DRVPOS'},
  {name: 'gseg', label: 'GSEG'},
  {name: 'gfsVehicleDates.sorp', label: 'SORP'},
  {name: 'gfsVehicleDates.eop', label: 'EOP'},
  {name: 'gfsVehicleDates.sos', label: 'SOS'},
  {name: 'engFam', label: 'ENGFAM'},
  {name: 'engRpo', label: 'ENGRPO'},
  {name: 'engDsp', label: 'ENGDSP'},
  {name: 'engCyl', label: 'ENGCYL'},
  {name: 'vfuel', label: 'VFUEL'},
  {name: 'engCam', label: 'ENGCAM'},
  {name: 'engPwr', label: 'ENGPWR'},
  {name: 'engTorq', label: 'ENGTORQ'},
  {name: 'injection', label: 'INJECTION'},
  {name: 'boost', label: 'BOOST'},
  {name: 'tranFam', label: 'TRANFAM'},
  {name: 'tranRpo', label: 'TRANRPO'},
  {name: 'tranName', label: 'TRANNAME'},
  {name: 'tranType', label: 'TRANTYPE'},
  {name: 'tranSpeed', label: 'TRANSPEED'},
  {name: 'cellFam', label: 'CELLFAM'},
  {name: 'batRpo', label: 'BATRPO'},
  {name: 'batDesc', label: 'BATDESC'},
  {name: 'elecFam', label: 'ELECFAM'},
  {name: 'elecRpo', label: 'ELECRPO'},
  {name: 'elecDesc', label: 'ELECDESC'},
  {name: 'priDrvRpo', label: 'PRIDRVRPO'},
  {name: 'priDrvDesc', label: 'PRIDRVDESC'},
  {name: 'secDrvRpo', label: 'SECDRVRPO'},
  {name: 'secDrvDesc', label: 'SECDRVDESC'},
  {name: 'driveType', label: 'DRIVETYPE'},
  {name: 'gfsPropulsionDates.propSorp', label: 'PROPSORP'},
  {name: 'gfsPropulsionDates.propEop', label: 'PROPEOP'},
  {name: 'gfsPropulsionDates.propSos', label: 'PROPSOS'},
  {name: 'gfsPropulsionDates.propFirstMy', label: 'PROPFIRSTMY'},
  {name: 'gfsPropulsionDates.propLastMy', label: 'PROPLASTMY'}];

export const VEHICLE_COLUMN_HEADERS: ColumnHeader[] = [
  {name: 'recId', label: 'RECID'},
  {name: 'iteration', label: 'ITERATION'},
  {name: 'modDate', label: 'MODDATE'},
  {name: 'recStat', label: 'RECSTAT'},
  {name: 'portType', label: 'PORT_TYPE'},
  {name: 'portStat', label: 'PORTSTAT'},
  {name: 'mktGrp', label: 'MKTGRP'},
  {name: 'fcOrg', label: 'FCORG'},
  {name: 'fcResp', label: 'FCRESP'},
  {name: 'region', label: 'REGION'},
  {name: 'subRegion', label: 'SUBREGION'},
  {name: 'sevenKeyFields.plngMkt', label: 'PLNGMKT'},
  {name: 'sevenKeyFields.brand', label: 'BRAND'},
  {name: 'sevenKeyFields.lbrand', label: 'LBRAND'},
  {name: 'sevenKeyFields.mktDiv', label: 'MKTDIV'},
  {name: 'arch', label: 'ARCH'},
  {name: 'sevenKeyFields.prgmCode', label: 'PRGMCODE'},
  {name: 'sevenKeyFields.bodyStyle', label: 'BODYSTYLE'},
  {name: 'sevenKeyFields.styleCode', label: 'STYLECODE'},
  {name: 'drvPos', label: 'DRVPOS'},
  {name: 'vehType', label: 'VEHTYPE'},
  {name: 'gseg', label: 'GSEG'},
  {name: 'gfsVehicleDate.sorp', label: 'SORP'},
  {name: 'gfsVehicleDate.eop', label: 'EOP'},
  {name: 'gfsVehicleDate.sos', label: 'SOS'},
  {name: 'gfsVehicleDate.eos', label: 'EOS'},
  {name: 'gfsVehicleDate.firstMy', label: 'FIRSTMY'},
  {name: 'gfsVehicleDate.lastMy', label: 'LASTMY'},
  {name: 'tos', label: 'TOS'},
  {name: 'engCtr', label: 'ENGCTR'}
];

export const VEHICLE_COLDEF: ColDef[] = [
  {field: 'vehScenId', headerName: 'Vehicle Scenario ID', lockPosition: true, filter: AutocompleteFilterComponent},
  {field: 'vehScenName', headerName: 'Vehicle Scenario Name', lockPosition: true, filter: AutocompleteFilterComponent},
  {field: 'fcorg', headerName: 'FCORG', lockPosition: true, filter: AutocompleteFilterComponent},
  {
    field: 'industryScenario.id',
    headerName: 'Industry Scenario ID',
    lockPosition: true,
    filter: AutocompleteFilterComponent
  },
  {
    field: 'industryScenario.industryScenarioName',
    headerName: 'Industry Scenario Name',
    lockPosition: true,
    filter: AutocompleteFilterComponent
  },
];

export const VEHICLE_COLDEF_NO_FILTER: ColDef[] = [
  {field: 'vehScenId', headerName: 'Vehicle Scenario ID', lockPosition: true},
  {field: 'vehScenName', headerName: 'Vehicle Scenario Name', lockPosition: true},
  {field: 'fcorg', headerName: 'FCORG', lockPosition: true},
  {
    field: 'industryScenario.id',
    headerName: 'Industry Scenario ID',
    lockPosition: true
  },
  {
    field: 'industryScenario.industryScenarioName',
    headerName: 'Industry Scenario Name',
    lockPosition: true
  },
];

export const VEHICLE_PROPULSION_COLDEF: ColDef[] = [
  {field: 'vehScenId', headerName: 'Vehicle Scenario ID', lockPosition: true, filter: AutocompleteFilterComponent},
  {field: 'vehScenName', headerName: 'Vehicle Scenario Name', lockPosition: true, filter: AutocompleteFilterComponent},
  {field: 'fcorg', headerName: 'FCORG', lockPosition: true, filter: AutocompleteFilterComponent},
  {
    field: 'propulsionScenario.id',
    headerName: 'Propulsion Scenario ID',
    lockPosition: true,
    filter: AutocompleteFilterComponent
  },
  {
    field: 'propulsionScenario.propulsionScenarioName',
    headerName: 'Propulsion Scenario Name',
    lockPosition: true,
    filter: AutocompleteFilterComponent
  },
];

export const VEHICLE_PROPULSION_COLDEF_NO_FILTER: ColDef[] = [
  {field: 'vehScenId', headerName: 'Vehicle Scenario ID', lockPosition: true},
  {field: 'vehScenName', headerName: 'Vehicle Scenario Name', lockPosition: true},
  {field: 'fcorg', headerName: 'FCORG', lockPosition: true},
  {
    field: 'propulsionScenario.id',
    headerName: 'Propulsion Scenario ID',
    lockPosition: true
  },
  {
    field: 'propulsionScenario.propulsionScenarioName',
    headerName: 'Propulsion Scenario Name',
    lockPosition: true
  },
];

export const INDUSTRY_COLDEF: ColDef[] = [
  {field: 'id', headerName: 'Industry Scenario ID', lockPosition: true, filter: AutocompleteFilterComponent},
  {field: 'forecastOrganization', headerName: 'FCORG', lockPosition: true, filter: AutocompleteFilterComponent},
  {
    field: 'industryScenarioName',
    headerName: 'Industry Scenario Name',
    lockPosition: true,
    filter: AutocompleteFilterComponent
  },
];

export const INDUSTRY_COLDEF_NO_FILTER: ColDef[] = [
  {field: 'id', headerName: 'Industry Scenario ID', lockPosition: true},
  {field: 'forecastOrganization', headerName: 'FCORG', lockPosition: true},
  {
    field: 'industryScenarioName',
    headerName: 'Industry Scenario Name',
    lockPosition: true
  },
];

export const PROPULSION_COLDEF: ColDef[] = [
  {field: 'id', headerName: 'Propulsion Scenario ID', lockPosition: true},
  {field: 'forecastOrganization', headerName: 'FCORG', lockPosition: true},
  {field: 'propulsionScenarioName', headerName: 'Propulsion Scenario Name', lockPosition: true},
];

export const FCORG_BLACKLIST: string[] = ['GFI_PTSTUDY', 'GMBUDGET', 'HUMMER', 'OPEL/VAUXHALL', 'SAAB', 'DELETE', 'US'];
export const NON_TVP_FCORGS: string[] = ['CHEV CAD EUROPE', 'GME', 'GME-NONGM', 'GMGC', 'GMGC-NONGM', 'GMI', 'GMI-NONGM', 'GMNA', 'GMNA-NONGM', 'GMSA', 'GMSA-NONGM']

export enum ScenarioSelectionSource {
  FCORG,
  GLOBAL,
  TVP,
}

export enum PortfolioTypes {
  BusinessPlan,
  TVP,
}

export const EXCEL_YELLOW = "#FFFF99"
export const EXCEL_GREEN = "#CCFFCC"
